import { Lead, Contact, Deal } from '../types';

export interface SearchResult {
  id: string;
  type: 'lead' | 'contact' | 'deal';
  title: string;
  subtitle: string;
  entity: Lead | Contact | Deal;
  matchScore: number;
}

export interface SearchOptions {
  query: string;
  leads?: Lead[];
  contacts?: Contact[];
  deals?: Deal[];
  limit?: number;
}

/**
 * Calculate Levenshtein distance for fuzzy matching
 */
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix: number[][] = [];
  const len1 = str1.length;
  const len2 = str2.length;

  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[len2][len1];
};

/**
 * Calculate similarity score (0-1) between two strings
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1;
  const distance = levenshteinDistance(str1, str2);
  return 1 - distance / maxLength;
};

/**
 * Check if query matches text with fuzzy matching
 */
export const fuzzyMatch = (text: string | undefined | null, query: string | undefined | null, threshold: number = 0.4): { matches: boolean; score: number } => {
  // Check for null/undefined BEFORE calling toLowerCase
  if (!text || !query || typeof text !== 'string' || typeof query !== 'string') {
    return { matches: false, score: 0 };
  }
  
  const lowerText = text.toLowerCase().trim();
  const lowerQuery = query.toLowerCase().trim();
  
  if (!lowerQuery || !lowerText) {
    return { matches: false, score: 0 };
  }
  
  // Exact match (highest priority)
  if (lowerText === lowerQuery) {
    return { matches: true, score: 1.0 };
  }
  
  // Contains match (high priority)
  if (lowerText.includes(lowerQuery)) {
    return { matches: true, score: 0.95 };
  }
  
  // Starts with match
  if (lowerText.startsWith(lowerQuery)) {
    return { matches: true, score: 0.9 };
  }
  
  // Word-by-word matching
  const textWords = lowerText.split(/\s+/).filter(w => w.length > 0);
  const queryWords = lowerQuery.split(/\s+/).filter(w => w.length > 0);
  
  if (queryWords.length > 0) {
    let wordMatches = 0;
    let exactWordMatches = 0;
    
    for (const queryWord of queryWords) {
      let found = false;
      for (const textWord of textWords) {
        if (textWord === queryWord) {
          exactWordMatches++;
          found = true;
          break;
        } else if (textWord.includes(queryWord) || queryWord.includes(textWord)) {
          wordMatches++;
          found = true;
          break;
        }
      }
    }
    
    if (exactWordMatches === queryWords.length) {
      return { matches: true, score: 0.85 };
    }
    
    if (wordMatches > 0) {
      const wordScore = wordMatches / queryWords.length;
      if (wordScore >= 0.5) {
        return { matches: true, score: wordScore * 0.8 };
      }
    }
  }
  
  // Fuzzy similarity matching for queries (3+ chars) - only for reasonable lengths
  if (lowerQuery.length >= 3 && lowerQuery.length <= 20 && lowerText.length <= 100) {
    const similarity = calculateSimilarity(lowerText, lowerQuery);
    if (similarity >= threshold) {
      return { matches: true, score: similarity };
    }
    
    // Check if any word in text is similar to query (limit to first 10 words for performance)
    const wordsToCheck = textWords.slice(0, 10);
    for (const textWord of wordsToCheck) {
      if (textWord.length >= 3 && textWord.length <= 50) {
        const wordSimilarity = calculateSimilarity(textWord, lowerQuery);
        if (wordSimilarity >= threshold) {
          return { matches: true, score: wordSimilarity * 0.7 };
        }
      }
    }
  }
  
  // For very short queries (1-2 chars), only exact matches
  if (lowerQuery.length <= 2) {
    // Check if any word starts with the query
    for (const textWord of textWords) {
      if (textWord.startsWith(lowerQuery)) {
        return { matches: true, score: 0.6 };
      }
    }
  }
  
  return { matches: false, score: 0 };
};

export interface SearchField {
  text: string | undefined | null;
  weight: number;
}

/** Score a query against multiple weighted text fields; returns best weighted score or 0. */
export function scoreMultiFieldMatch(query: string, fields: SearchField[], threshold = 0.3): number {
  const q = query.trim();
  if (!q) return 0;
  let best = 0;
  for (const { text, weight } of fields) {
    const { matches, score } = fuzzyMatch(text, q, threshold);
    if (matches) best = Math.max(best, score * weight);
  }
  return best;
}

/** Strip HTML and collapse whitespace for comment/search indexing. */
export function buildSearchableText(htmlOrText: string | undefined | null): string {
  if (!htmlOrText) return '';
  return htmlOrText
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Search across leads, contacts, and deals
 */
export const searchCRM = (options: SearchOptions): SearchResult[] => {
  const { query, leads = [], contacts = [], deals = [], limit = 20 } = options;
  const lowerQuery = query.toLowerCase().trim();
  
  if (!lowerQuery || lowerQuery.length === 0) {
    return [];
  }

  // Early return for very short queries to avoid expensive calculations
  if (lowerQuery.length > 50) return []; // Sanity check - queries shouldn't be this long

  // Performance check: if we have too many items, limit the search
  const totalItems = leads.length + contacts.length + deals.length;
  if (totalItems > 5000) {
    console.warn(`Searching through ${totalItems} items - this may be slow. Consider filtering data first.`);
  }

  const results: SearchResult[] = [];

  // Search leads (limit to first 1000 for performance)
  const leadsToSearch = leads.slice(0, 1000);
  leadsToSearch.forEach(lead => {
    let score = 0;

    // Name matching (highest weight)
    const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim();
    const firstNameMatch = fuzzyMatch(lead.firstName, lowerQuery, 0.3);
    const lastNameMatch = fuzzyMatch(lead.lastName, lowerQuery, 0.3);
    const fullNameMatch = fuzzyMatch(fullName || undefined, lowerQuery, 0.3);
    
    if (fullNameMatch.matches) {
      score += 10 * fullNameMatch.score;
    } else if (firstNameMatch.matches) {
      score += 8 * firstNameMatch.score;
    } else if (lastNameMatch.matches) {
      score += 8 * lastNameMatch.score;
    }

    // Email matching
    const emailMatch = fuzzyMatch(lead.email, lowerQuery, 0.4);
    if (emailMatch.matches) {
      score += 5 * emailMatch.score;
    }

    // Phone matching (exact or partial)
    if (lead.phone) {
      const phoneStr = String(lead.phone); // Ensure phone is a string
      const phoneDigits = phoneStr.replace(/\D/g, '');
      const queryDigits = lowerQuery.replace(/\D/g, '');
      if (phoneDigits.includes(queryDigits) || queryDigits.length >= 3 && phoneDigits.includes(queryDigits)) {
        score += 5;
      }
    }

    // Company matching
    if (lead.company) {
      const companyMatch = fuzzyMatch(lead.company, lowerQuery, 0.3);
      if (companyMatch.matches) {
        score += 4 * companyMatch.score;
      }
    }

    // Location matching
    const locationMatch = fuzzyMatch(lead.location, lowerQuery, 0.3);
    if (locationMatch.matches) {
      score += 3 * locationMatch.score;
    }

    if (score > 0) {
      results.push({
        id: lead.id || '',
        type: 'lead',
        title: `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unnamed Lead',
        subtitle: lead.company || lead.email || lead.location || '',
        entity: lead,
        matchScore: score,
      });
    }
  });

  // Search contacts (limit to first 1000 for performance)
  const contactsToSearch = contacts.slice(0, 1000);
  contactsToSearch.forEach(contact => {
    let score = 0;

    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    const firstNameMatch = fuzzyMatch(contact.firstName, lowerQuery, 0.3);
    const lastNameMatch = fuzzyMatch(contact.lastName, lowerQuery, 0.3);
    const fullNameMatch = fuzzyMatch(fullName || undefined, lowerQuery, 0.3);
    
    if (fullNameMatch.matches) {
      score += 10 * fullNameMatch.score;
    } else if (firstNameMatch.matches) {
      score += 8 * firstNameMatch.score;
    } else if (lastNameMatch.matches) {
      score += 8 * lastNameMatch.score;
    }

    const emailMatch = fuzzyMatch(contact.email, lowerQuery, 0.4);
    if (emailMatch.matches) {
      score += 5 * emailMatch.score;
    }

    if (contact.phone) {
      const phoneDigits = contact.phone.replace(/\D/g, '');
      const queryDigits = lowerQuery.replace(/\D/g, '');
      if (phoneDigits.includes(queryDigits) || queryDigits.length >= 3 && phoneDigits.includes(queryDigits)) {
        score += 5;
      }
    }

    if (contact.company) {
      const companyMatch = fuzzyMatch(contact.company, lowerQuery, 0.3);
      if (companyMatch.matches) {
        score += 4 * companyMatch.score;
      }
    }

    if (score > 0) {
      results.push({
        id: contact.id || '',
        type: 'contact',
        title: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unnamed Contact',
        subtitle: contact.company || contact.email || '',
        entity: contact,
        matchScore: score,
      });
    }
  });

  // Search deals (limit to first 1000 for performance)
  const dealsToSearch = deals.slice(0, 1000);
  dealsToSearch.forEach(deal => {
    let score = 0;

    const titleMatch = fuzzyMatch(deal.title, lowerQuery, 0.3);
    if (titleMatch.matches) {
      score += 10 * titleMatch.score;
    }

    const contactMatch = fuzzyMatch(deal.contactName, lowerQuery, 0.3);
    if (contactMatch.matches) {
      score += 5 * contactMatch.score;
    }

    if (deal.description) {
      const descMatch = fuzzyMatch(deal.description, lowerQuery, 0.3);
      if (descMatch.matches) {
        score += 3 * descMatch.score;
      }
    }

    if (score > 0) {
      results.push({
        id: deal.id || '',
        type: 'deal',
        title: deal.title || 'Untitled Deal',
        subtitle: `$${(deal.value || 0).toLocaleString()} - ${deal.contactName || 'Unknown Contact'}`,
        entity: deal,
        matchScore: score,
      });
    }
  });

  // Sort by match score and limit results
  return results
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);
};

/**
 * Highlight matching text in a string
 */
export const highlightMatch = (text: string, query: string): string => {
  if (!query || !text) return text || '';
  
  // Escape special regex characters in query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
  try {
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  } catch (error) {
    // If regex fails, return original text
    console.warn('Highlight match error:', error);
    return text;
  }
};
